<template>
  <div class="playground-container">
    <div class="header">
      <h1 class="mb-4">Component Playground - Testing Component</h1>
      <a :href="videoFormatterUrl" target="_blank" rel="noopener noreferrer" class="redirect-btn">Go to Video Formatter</a>
      <div class="controls-grid mt-4">
        <button class="redirect-btn btn-success" @click="onClickSimulate">Simulate File Upload</button>
        <div class="control-group">
          <label>Simulate No Of Error Items</label>
          <input type="number" v-model.number="simulateNoOfError" min="0" />
        </div>
      </div>
    </div>

    <div class="data-section mb-4">
      <details>
        <summary>Update to DB Data (Both Image and Video)</summary>
        <pre>{{ updateToDbData ? formatData(updateToDbData) : 'No data' }}</pre>
      </details>
    </div>

    <!-- Image Uploader Section -->
    <details class="card">
      <summary class="card-header card-summary">
        <h2>Image Uploader</h2>
        <span class="badge">Type: Image</span>
      </summary>
      
      <details class="section" open>
        <summary class="section-summary">
          <h3>Configuration</h3>
        </summary>
        <div class="section-body controls-section">
          <div class="controls-grid">
            <div class="control-group">
              <label>Limit (Max Items)</label>
              <input type="number" v-model.number="imgConfig.limit" min="1" />
            </div>
            <div class="control-group">
              <label>Max Size (MB)</label>
              <input type="number" v-model.number="imgConfig.maxSize" min="1" />
            </div>
            <div class="control-group">
              <label>Aspect Ratio (e.g., 1:1)</label>
              <input type="text" v-model="imgConfig.aspectRatioStr" />
            </div>
            <div class="control-group">
              <label>Item Style (CSS)</label>
              <input type="text" v-model="imgConfig.galleryItemStyle" />
            </div>
            <div class="control-group">
              <label>Grid Template (CSS)</label>
              <input type="text" v-model="imgConfig.gridTemplateColumns" />
            </div>
            <div class="control-group">
              <label>File Usage Type</label>
              <input type="text" v-model="imgConfig.fileUsageType" />
            </div>
            <div class="control-group">
              <label>File Path Endpoint</label>
              <input type="text" v-model="imgConfig.filePathEndpoint" />
            </div>
            <div class="control-group full-width">
              <label>Requirement Message</label>
              <input type="text" v-model="imgConfig.requirementMessage" />
            </div>
            
            <div class="control-group checkbox-group">
              <label>
                <input type="checkbox" v-model="imgConfig.isRequired" />
                Is Required
              </label>
            </div>
            <div class="control-group checkbox-group">
              <label>
                <input type="checkbox" v-model="imgConfig.isDisabled" />
                Disabled
              </label>
            </div>
            <div class="control-group checkbox-group">
              <label>
                <input type="checkbox" v-model="imgConfig.singleAddButton" />
                Single Add Button
              </label>
            </div>
            <div class="control-group checkbox-group">
              <label>
                <input type="checkbox" v-model="imgConfig.isShowCover" />
                Show Cover
              </label>
            </div>
          </div>
        </div>
      </details>

      <details class="section" open>
        <summary class="section-summary">
          <h3>Preview</h3>
        </summary>
        <div class="section-body component-section">
          <div class="component-wrapper">
            <MediaUploader
              :MediaArray="images"
              :limit="imgConfig.limit"
              :maxSize="imgConfig.maxSize"
              :aspectRatioStr="imgConfig.aspectRatioStr"
              :galleryItemStyle="imgConfig.galleryItemStyle"
              :gridTemplateColumns="imgConfig.gridTemplateColumns"
              :requirementMessage="imgConfig.requirementMessage"
              :isRequired="imgConfig.isRequired"
              :isDisabled="imgConfig.isDisabled"
              :singleAddButton="imgConfig.singleAddButton"
              :isShowCover="imgConfig.isShowCover"
              mediaType="image"
              ref="imgUploader"
              :fileUsageType="imgConfig.fileUsageType"
              :filePathEndpoint="imgConfig.filePathEndpoint"
              :reject-records="imgRejectRecords"
              @update="(cmd, files) => updateImageData(cmd, files, 'image')"
            />
            <MediaRejectBanner :records="imgRejectRecords" />
          </div>
        </div>
      </details>

      <div class="data-section">
        <details open>
          <summary>Usage Example</summary>
          <pre>{{ imageUsageExample }}</pre>
        </details>
      </div>
      <div class="data-section">
        <details>
          <summary>Reject Banner Records</summary>
          <pre>{{ formatData(imgRejectRecords) }}</pre>
        </details>
      </div>
      <div class="data-section">
        <details>
          <summary>MediaArray Data ({{ images.length }} items)</summary>
          <pre>{{ formatData(images) }}</pre>
        </details>
      </div>
      <div class="data-section">
        <details>
          <summary>Pending Upload Data ({{ pendingUploadFiles["image"].length }} items)</summary>
          <pre>{{ formatData(pendingUploadFiles["image"]) }}</pre>
        </details>
      </div>
      <div class="data-section">
        <details>
          <summary>DB Update Commands ({{ dbUpdateCommands["image"].length }} items)</summary>
          <pre>{{ formatData(dbUpdateCommands["image"]) }}</pre>
        </details>
      </div>
    </details>

    <!-- Video Uploader Section -->
    <details class="card">
      <summary class="card-header card-summary">
        <h2>Video Uploader</h2>
        <span class="badge">Type: Video</span>
      </summary>

      <details class="section" open>
        <summary class="section-summary">
          <h3>Configuration</h3>
        </summary>
        <div class="section-body controls-section">
          <div class="controls-grid">
            <div class="control-group">
              <label>Limit (Max Items)</label>
              <input type="number" v-model.number="videoConfig.limit" min="1" />
            </div>
            <div class="control-group">
              <label>Max Size (MB)</label>
              <input type="number" v-model.number="videoConfig.maxSize" min="1" />
            </div>
            <div class="control-group">
              <label>Max Duration (Seconds)</label>
              <input type="number" v-model.number="videoConfig.maxDuration" min="1" />
            </div>
            <div class="control-group">
              <label>Aspect Ratio (e.g., 16:9)</label>
              <input type="text" v-model="videoConfig.aspectRatioStr" />
            </div>
            <div class="control-group">
              <label>Cover Placeholder</label>
              <input type="text" v-model="videoConfig.coverPlaceholder" />
            </div>
            <div class="control-group">
              <label>Item Style (CSS)</label>
              <input type="text" v-model="videoConfig.galleryItemStyle" />
            </div>
            <div class="control-group">
              <label>Grid Template (CSS)</label>
              <input type="text" v-model="videoConfig.gridTemplateColumns" />
            </div>
            <div class="control-group">
              <label>File Usage Type</label>
              <input type="text" v-model="videoConfig.fileUsageType" />
            </div>
            <div class="control-group">
              <label>File Path Endpoint</label>
              <input type="text" v-model="videoConfig.filePathEndpoint" />
            </div>
            <div class="control-group">
              <label>File Thumbnail Usage Type</label>
              <input type="text" v-model="videoConfig.fileThumbnailUsageType" />
            </div>
            <div class="control-group">
              <label>File Thumbnail Path Endpoint</label>
              <input type="text" v-model="videoConfig.fileThumbnailPathEndpoint" />
            </div>

            <div class="control-group full-width">
              <label>Requirement Message</label>
              <input type="text" v-model="videoConfig.requirementMessage" />
            </div>
            
            <div class="control-group checkbox-group">
              <label>
                <input type="checkbox" v-model="videoConfig.isRequired" />
                Is Required
              </label>
            </div>
            <div class="control-group checkbox-group">
              <label>
                <input type="checkbox" v-model="videoConfig.isDisabled" />
                Disabled
              </label>
            </div>
            <div class="control-group checkbox-group">
              <label>
                <input type="checkbox" v-model="videoConfig.singleAddButton" />
                Single Add Button
              </label>
            </div>
            <div class="control-group checkbox-group">
              <label>
                <input type="checkbox" v-model="videoConfig.isShowCover" />
                Show Cover
              </label>
            </div>
          </div>
        </div>
      </details>

      <details class="section" open>
        <summary class="section-summary">
          <h3>Preview</h3>
        </summary>
        <div class="section-body component-section">
          <div class="component-wrapper">
            <MediaUploader
              :MediaArray="videos"
              :limit="videoConfig.limit"
              :maxSize="videoConfig.maxSize"
              :maxDuration="videoConfig.maxDuration"
              :aspectRatioStr="videoConfig.aspectRatioStr"
              :galleryItemStyle="videoConfig.galleryItemStyle"
              :gridTemplateColumns="videoConfig.gridTemplateColumns"
              :coverPlaceholder="videoConfig.coverPlaceholder"
              :isRequired="videoConfig.isRequired"
              :isDisabled="videoConfig.isDisabled"
              :singleAddButton="videoConfig.singleAddButton"
              :isShowCover="videoConfig.isShowCover"
              mediaType="video"
              :fileUsageType="videoConfig.fileUsageType"
              :filePathEndpoint="videoConfig.filePathEndpoint"
              :fileThumbnailUsageType="videoConfig.fileThumbnailUsageType"
              :fileThumbnailPathEndpoint="videoConfig.fileThumbnailPathEndpoint"
              :reject-records="videoRejectRecords"
              @update="(cmd, files) => updateImageData(cmd, files, 'video')"
            />
            <MediaRejectBanner :records="videoRejectRecords" />
          </div>
        </div>
      </details>

      <div class="data-section">
        <details open>
          <summary>Usage Example</summary>
          <pre>{{ videoUsageExample }}</pre>
        </details>
      </div>
      <div class="data-section">
        <details>
          <summary>Reject Banner Records</summary>
          <pre>{{ formatData(videoRejectRecords) }}</pre>
        </details>
      </div>
      <div class="data-section">
        <details>
          <summary>MediaArray Data ({{ videos.length }} items)</summary>
          <pre>{{ formatData(videos) }}</pre>
        </details>
      </div>
      <div class="data-section">
        <details>
          <summary>Pending Upload Data ({{ pendingUploadFiles["video"].length }} items)</summary>
          <pre>{{ formatData(pendingUploadFiles["video"]) }}</pre>
        </details>
      </div>
      <div class="data-section">
        <details>
          <summary>DB Update Commands ({{ dbUpdateCommands["video"].length }} items)</summary>
          <pre>{{ formatData(dbUpdateCommands["video"]) }}</pre>
        </details>
      </div>
    </details>

    <!-- Field Rejection Feedback Section -->
    <details class="card" open>
      <summary class="card-header card-summary">
        <h2>Field Rejection Feedback</h2>
        <span class="badge">Mode: {{ frfConfig.mode }}</span>
      </summary>

      <details class="section" open>
        <summary class="section-summary">
          <h3>Configuration</h3>
        </summary>
        <div class="section-body controls-section">
          <div class="controls-grid">
            <div class="control-group">
              <label>Mode</label>
              <select v-model="frfConfig.mode">
                <option value="admin">admin</option>
                <option value="customer">customer</option>
              </select>
            </div>
            <div class="control-group">
              <label>Actor</label>
              <input type="text" v-model="frfConfig.actor" />
            </div>
            <div class="control-group full-width">
              <label>Reason (value)</label>
              <input type="text" v-model="frfConfig.value" />
            </div>
            <div class="control-group checkbox-group">
              <label>
                <input type="checkbox" v-model="frfConfig.rejected" />
                Rejected
              </label>
            </div>
            <div class="control-group checkbox-group">
              <label>
                <input type="checkbox" v-model="frfConfig.useTemplates" />
                Pass Templates
              </label>
            </div>
          </div>
        </div>
      </details>

      <details class="section" open>
        <summary class="section-summary">
          <h3>Preview</h3>
        </summary>
        <div class="section-body component-section">
          <div class="component-wrapper frf-preview">
            <p v-if="frfConfig.mode === 'customer' && !frfConfig.rejected" class="frf-empty-hint">
              Customer + rejected=false renders nothing.
            </p>
            <FieldRejectionFeedback
              v-model="frfConfig.value"
              :mode="frfConfig.mode"
              :rejected.sync="frfConfig.rejected"
              :templates="frfTemplates"
              :actor="frfConfig.actor"
            />
          </div>
        </div>
      </details>

      <div class="data-section">
        <details open>
          <summary>Usage Example</summary>
          <pre>{{ frfUsageExample }}</pre>
        </details>
      </div>

      <div class="data-section">
        <details>
          <summary>View Model Data</summary>
          <pre>{{ formatData(frfModelData) }}</pre>
        </details>
      </div>
    </details>

    <!-- Media Approval Gallery Section -->
    <details class="card" open>
      <summary class="card-header card-summary">
        <h2>Media Approval Gallery</h2>
        <span class="badge">Mode: {{ magConfig.mode }} · {{ magConfig.mediaType }}</span>
      </summary>

      <details class="section" open>
        <summary class="section-summary">
          <h3>Configuration</h3>
        </summary>
        <div class="section-body controls-section">
          <div class="controls-grid">
            <div class="control-group">
              <label>Mode</label>
              <select v-model="magConfig.mode">
                <option value="admin">admin</option>
                <option value="customer">customer (client read-only)</option>
              </select>
            </div>
            <div class="control-group">
              <label>Media Type</label>
              <select v-model="magConfig.mediaType">
                <option value="image">image</option>
                <option value="video">video</option>
              </select>
            </div>
            <div class="control-group">
              <label>Title</label>
              <input type="text" v-model="magConfig.title" />
            </div>
            <div class="control-group">
              <label>Rejection Actor</label>
              <input type="text" v-model="magConfig.rejectionActor" />
            </div>
            <div class="control-group">
              <label>Item Style (CSS)</label>
              <input type="text" v-model="magConfig.galleryItemStyle" />
            </div>
            <div class="control-group">
              <label>Grid Template Columns</label>
              <input type="text" v-model="magConfig.gridTemplateColumns" />
            </div>
            <div class="control-group checkbox-group">
              <label>
                <input type="checkbox" v-model="magConfig.isShowCover" />
                Show Cover Badge
              </label>
            </div>
            <div class="control-group checkbox-group">
              <label>
                <input type="checkbox" v-model="magConfig.useTemplates" />
                Pass Rejection Templates
              </label>
            </div>
          </div>
        </div>
      </details>

      <details class="section" open>
        <summary class="section-summary">
          <h3>Preview</h3>
        </summary>
        <div class="section-body component-section">
          <div class="component-wrapper">
            <MediaApprovalGallery
              :MediaArray="magMediaArray"
              :mode="magConfig.mode"
              :media-type="magConfig.mediaType"
              :title="magConfig.title"
              :is-show-cover="magConfig.isShowCover"
              :gallery-item-style="magConfig.galleryItemStyle"
              :grid-template-columns="magConfig.gridTemplateColumns"
              :approval-content-results="magApprovalResults"
              :approval-content-result-drafts="magApprovalDrafts"
              :rejection-templates="magRejectionTemplates"
              :rejection-actor="magConfig.rejectionActor"
              @approval-draft-change="onMagApprovalDraftChange"
            />
          </div>
        </div>
      </details>

      <div class="data-section">
        <details open>
          <summary>Usage Example</summary>
          <pre>{{ magUsageExample }}</pre>
        </details>
      </div>

      <div class="data-section">
        <details open>
          <summary>Drafts + last emit</summary>
          <pre>{{ formatData(magModelData) }}</pre>
        </details>
      </div>
    </details>
    
  </div>
</template>

<script>
import MediaUploader from '@/components/form/upload/imgUploader/mediaUploader.vue';
import MediaApprovalGallery from '@/components/form/upload/imgUploader/mediaApprovalGallery.vue';
import MediaRejectBanner from '@/components/form/upload/imgUploader/mediaRejectBanner.vue';
import {
  getInitImageDataObj,
  getInitVideoDataObj
} from '@/_helpers/media-uploader-utils.js';
import { getUpdateOSSUrlToDbRequest } from '@/_helpers/utility.js';
import FieldRejectionFeedback from '@/components/FieldRejectionFeedback/FieldRejectionFeedback.vue';

// Sample templates (title/content shape matches Approval reject templates)
const SAMPLE_FRF_TEMPLATES = [
  { id: 't1', title: 'Too Much Content or Unclear', content: 'Please simplify the content; it is currently too dense or unclear.' },
  { id: 't2', title: 'Sensitive Words', content: 'Please remove or revise sensitive wording before resubmitting.' },
  { id: 't3', title: 'Customer Needs to Modify Content', content: 'Customer needs to modify the submitted content.' },
  { id: 't4', title: 'Missing Specific Materials', content: 'Please upload the missing required materials.' },
  { id: 't5', title: 'Ad Size Error', content: 'Ad dimensions do not meet the required size.' },
  { id: 't6', title: 'Mobile Ad Size Error', content: 'Mobile ad dimensions do not meet the required size.' },
  { id: 't7', title: 'Image Blurry', content: 'The image is blurry; please upload a clearer asset.' },
  { id: 't8', title: 'Missing or Update LOGO', content: 'Please add or update the LOGO.' },
  { id: 't9', title: 'Contains inappropriate graphic', content: 'Please remove inappropriate graphics.' },
  { id: 't10', title: 'Remove QR code/phone number/website', content: 'Please remove QR code, phone number, or website from the creative.' },
];

export default {
  name: 'ComponetPlayground',
  components: {
    MediaUploader,
    MediaApprovalGallery,
    MediaRejectBanner,
    FieldRejectionFeedback,
  },
  data() {
    return {
      // Model Data
      images: [],
      videos: [],
      magApprovalResults: [],
      magApprovalDrafts: {},
      magLastEmit: null,
      imgRejectRecords: [
        {
          mediaId: '1',
          rejectReason: 'Blurry product shot',
          previewSrc: 'https://cdn.adsalecdn.com/EMP/Backend/Upload/VideoCapture/1036654_20251210143706146.webp',
          fileName: 'sample_image_1.webp',
          mediaType: 'image',
          actor: 'Admin Chan',
        },
        {
          mediaId: '2',
          rejectReason: '',
          previewSrc: 'https://cdn.adsalecdn.com/EMP/Backend/Upload/VideoCapture/1036654_20251205165818667.webp',
          fileName: 'sample_image_2.webp',
          mediaType: 'image',
        },
        {
          mediaId: '3',
          rejectReason: 'Wrong crop',
          previewSrc: 'https://cdn.adsalecdn.com/EMP/Backend/Upload/VideoCapture/1036654_20251205171407643.webp',
          fileName: 'sample_image_3.webp',
          mediaType: 'image',
        },
      ],
      videoRejectRecords: [
        {
          mediaId: '4',
          rejectReason: 'Video too dark',
          previewSrc: 'https://cdn.adsalecdn.com/EMP/Backend/Upload/Video/1000168_20251222092211762.mp4',
          fileName: 'sample_video.mp4',
          mediaType: 'video',
          actor: 'Admin Chan',
        },
        {
          mediaId: '5',
          rejectReason: '',
          previewSrc: 'https://cdn.adsalecdn.com/EMP/Backend/Upload/Video/1000168_20251222092211762.mp4',
          fileName: 'sample_video_ok.mp4',
          mediaType: 'video',
        },
        {
          mediaId: '6',
          rejectReason: 'Audio missing',
          previewSrc: 'https://cdn.adsalecdn.com/EMP/Backend/Upload/Video/1000168_20251222092211762.mp4',
          fileName: 'sample_video_2.mp4',
          mediaType: 'video',
        },
      ],
      
      dbUpdateCommands: {
        image: [],
        video: [],
      },

      pendingUploadFiles: {
        image: [],
        video: [],
      },

      updateToDbData: null,
      simulateNoOfError: 0,
      
      // Image Uploader Configuration
      imgConfig: {
        limit: 10,
        maxSize: 100,
        aspectRatioStr: '1:1',
        galleryItemStyle: 'height: 120px; width: 120px;',
        gridTemplateColumns: 'repeat(5, 1fr)',
        requirementMessage: ' (W) 1200 x (H) 600 pixels, support JPEG / JPG / PNG format',
        isRequired: false,
        isDisabled: false,
        singleAddButton: false,
        isShowCover: true,
        fileUsageType: 'ExhibitImage',
        filePathEndpoint: 'Image'
      },

      // Video Uploader Configuration
      videoConfig: {
        limit: 5,
        maxSize: 500,
        maxDuration: 60,
        aspectRatioStr: '16:9',
        galleryItemStyle: 'height: 104px; width: 185px;',
        gridTemplateColumns: 'repeat(5, 1fr)',
        coverPlaceholder: 'Cover Video',
        isRequired: false,
        isDisabled: false,
        singleAddButton: false,
        isShowCover: true,
        fileUsageType: 'ExhibitVideo',
        filePathEndpoint: 'Video',
        fileThumbnailUsageType: 'ExhibitVideo_Cover',
        fileThumbnailPathEndpoint: 'VideoCapture'
      },

      // Field Rejection Feedback Configuration
      frfConfig: {
        mode: 'admin',
        rejected: false,
        value: '',
        actor: 'Admin Chan',
        useTemplates: true,
      },

      // Media Approval Gallery Configuration
      magConfig: {
        mode: 'admin',
        mediaType: 'image',
        title: 'Product Photos',
        rejectionActor: 'Admin Chan',
        galleryItemStyle: 'height: 120px; width: 120px;',
        gridTemplateColumns: 'repeat(5, 1fr)',
        isShowCover: true,
        useTemplates: true,
      },
    };
  },
  computed: {
    videoFormatterUrl() {
      return process.env.VUE_APP_ROUTE_PATH + "video-formatter";
    },
    frfTemplates() {
      return this.frfConfig.useTemplates ? SAMPLE_FRF_TEMPLATES : [];
    },
    magMediaArray() {
      return this.magConfig.mediaType === 'video' ? this.videos : this.images;
    },
    magRejectionTemplates() {
      return this.magConfig.useTemplates ? SAMPLE_FRF_TEMPLATES : [];
    },
    frfModelData() {
      return {
        mode: this.frfConfig.mode,
        rejected: this.frfConfig.rejected,
        value: this.frfConfig.value,
        actor: this.frfConfig.actor,
        templates: this.frfTemplates,
      };
    },
    imageUsageExample() {
      const q = (v) => JSON.stringify(v ?? '');
      const c = this.imgConfig || {};
      return `<MediaUploader
        :MediaArray="images"
        :limit=${q(c.limit)}
        :maxSize=${q(c.maxSize)}
        :aspectRatioStr=${q(c.aspectRatioStr)}
        :galleryItemStyle=${q(c.galleryItemStyle)}
        :gridTemplateColumns=${q(c.gridTemplateColumns)}
        :requirementMessage=${q(c.requirementMessage)}
        :isRequired=${q(c.isRequired)}
        :isDisabled=${q(c.isDisabled)}
        :singleAddButton=${q(c.singleAddButton)}
        :isShowCover=${q(c.isShowCover)}
        mediaType="image"
        :fileUsageType=${q(c.fileUsageType)}
        :filePathEndpoint=${q(c.filePathEndpoint)}
        :reject-records="imgRejectRecords"
        @update="(cmd, files) => updateImageData(cmd, files, 'image')"
      />
      <MediaRejectBanner :records="imgRejectRecords" />`;
    },
    videoUsageExample() {
      const q = (v) => JSON.stringify(v ?? '');
      const c = this.videoConfig || {};
      return `<MediaUploader
        :MediaArray="videos"
        :limit=${q(c.limit)}
        :maxSize=${q(c.maxSize)}
        :maxDuration=${q(c.maxDuration)}
        :aspectRatioStr=${q(c.aspectRatioStr)}
        :galleryItemStyle=${q(c.galleryItemStyle)}
        :gridTemplateColumns=${q(c.gridTemplateColumns)}
        :coverPlaceholder=${q(c.coverPlaceholder)}
        :isRequired=${q(c.isRequired)}
        :isDisabled=${q(c.isDisabled)}
        :singleAddButton=${q(c.singleAddButton)}
        :isShowCover=${q(c.isShowCover)}
        mediaType="video"
        :fileUsageType=${q(c.fileUsageType)}
        :filePathEndpoint=${q(c.filePathEndpoint)}
        :fileThumbnailUsageType=${q(c.fileThumbnailUsageType)}
        :fileThumbnailPathEndpoint=${q(c.fileThumbnailPathEndpoint)}
        :reject-records="videoRejectRecords"
        @update="(cmd, files) => updateImageData(cmd, files, 'video')"
      />
      <MediaRejectBanner :records="videoRejectRecords" />`;
    },
    frfUsageExample() {
      const q = (v) => JSON.stringify(v ?? '');
      const c = this.frfConfig || {};
      const templatesAttr = c.useTemplates
        ? `\n  :templates="[{ id: 't1', label: 'Incomplete address' }]"`
        : '';
      return `<FieldRejectionFeedback
  v-model="reason"
  mode=${q(c.mode)}
  :rejected.sync="rejected"${templatesAttr}
  actor=${q(c.actor)}
/>`;
    },
    magUsageExample() {
      const q = (v) => JSON.stringify(v ?? '');
      const c = this.magConfig || {};
      const templatesAttr = c.mode === 'admin' && c.useTemplates
        ? `\n  :rejection-templates="[{ id: 't1', label: 'Blurry product shot' }]"`
        : '';
      const mediaVar = c.mediaType === 'video' ? 'videos' : 'images';
      return `<MediaApprovalGallery
  :MediaArray="${mediaVar}"
  mode=${q(c.mode)}
  media-type=${q(c.mediaType)}
  title=${q(c.title)}
  :is-show-cover=${q(c.isShowCover)}
  :gallery-item-style=${q(c.galleryItemStyle)}
  :grid-template-columns=${q(c.gridTemplateColumns)}
  :approval-content-results="approvalContentResults"
  :approval-content-result-drafts="approvalContentResultDrafts"${templatesAttr}
  rejection-actor=${q(c.rejectionActor)}
  @approval-draft-change="onApprovalDraftChange"
/>`;
    },
    magModelData() {
      return {
        config: this.magConfig,
        drafts: this.magApprovalDrafts,
        results: this.magApprovalResults,
        lastEmit: this.magLastEmit,
      };
    },
  },
  mounted() {
    // Initial Mock Data
    const sampleImages = [
      {
        id: '1',
        url: 'https://cdn.adsalecdn.com/EMP/Backend/Upload/VideoCapture/1036654_20251210143706146.webp',
        name: 'sample_image_1.webp',
        size: '',
        usageType: 'image',
        orderId: 1
      },
      {
        id: '2',
        url: 'https://cdn.adsalecdn.com/EMP/Backend/Upload/VideoCapture/1036654_20251205165818667.webp',
        name: 'sample_image_2.webp',
        size: '',
        usageType: 'image',
        orderId: 2
      },
      {
        id: '3',
        url: 'https://cdn.adsalecdn.com/EMP/Backend/Upload/VideoCapture/1036654_20251205171407643.webp',
        name: 'sample_image_3.webp',
        size: '',
        usageType: 'image',
        orderId: 3
      }
    ];

    for (const image of sampleImages) {
      const id = image.id;
      const cdnLink = image.url;
      const usageType = image.usageType;
      const orderId = image.orderId;
      this.images.push(
        getInitImageDataObj(image, { id, cdnLink, usageType, orderId })
      );
    }

    this.magApprovalResults = [
      {
        usageId: '1',
        approvalRequestResultId: 'arr-1',
        rejectReason: 'Blurry product shot',
      },
    ];
    this.magApprovalDrafts = { '1': 'Blurry product shot' };

    const sampleVideos = [
      {
        id: '4',
        videoUrl: 'https://cdn.adsalecdn.com/EMP/Backend/Upload/Video/1000168_20251222092211762.mp4',
        url: 'https://cdn.adsalecdn.com/EMP/Backend/Upload/VideoCapture/1036654_20251205171407643.webp', // Poster image
        name: 'sample_video.mp4',
        size: '500',
        usageType: 'video',
        orderId: 1
      }
    ];

    for (const video of sampleVideos) {
      const id = video.id;
      const cdnLink = video.videoUrl;
      const usageType = video.usageType;
      const orderId = video.orderId;
      const thumbnail = {
        id: video.id,
        url: video.url,
        usageType: 'video_cover',
        orderId: video.orderId
      };
      this.videos.push(
        getInitVideoDataObj(
          video,
          { id, cdnLink, usageType, orderId },
          thumbnail ? thumbnail : {},
          thumbnail ? { id: thumbnail.id, cdnLink: thumbnail.url, usageType: thumbnail.usageType, orderId: thumbnail.orderId } : {}
        )
      );
    }
  },
  watch: {
    images: {
      handler(newImages) {
        console.log("Images updated:", newImages);
      },
      deep: true
    },
    videos: {
      handler(newVideos) {
        console.log("Videos updated:", newVideos);
      },
      deep: true
    }
  },
  methods: {
    onMagApprovalDraftChange(payload) {
      this.magLastEmit = payload;
      if (!payload || payload.mediaId == null) return;
      this.$set(
        this.magApprovalDrafts,
        payload.mediaId,
        payload.rejectReason == null ? '' : String(payload.rejectReason),
      );
    },

    // [BLK] Only update the stored data arrays for later manipulation
    updateImageData(commands, pendingFiles, uploaderType) {
      this.dbUpdateCommands[uploaderType] = [...commands];
      this.pendingUploadFiles[uploaderType] = [...pendingFiles];
    },

    simulateUpload(pendingFiles, failedCount = 0) {
      return {
        status: 'SimulateUploadStatus',
        successfulList: pendingFiles.slice(failedCount).map(file => ({
          ...file,
          uploadedOssUrl: `/${file.pathEndpoint}/${file.id}_${file.fileName}`,
        })),
        failedList: pendingFiles.slice(0, failedCount),
      };
    },

    onClickSimulate() {
      const pendingFiles = [];
      for (const key in this.pendingUploadFiles) {
        pendingFiles.push(...this.pendingUploadFiles[key]);
      }

      const commands = [];
      for (const key in this.dbUpdateCommands) {
        commands.push(...this.dbUpdateCommands[key]);
      }

      const failedCount = this.simulateNoOfError;

      const res = {
        ...this.simulateUpload(pendingFiles, failedCount),
        commands,
      };
      const saveToDbRequest = getUpdateOSSUrlToDbRequest(res);

      this.simulateSaveToDb(saveToDbRequest);
    },

    simulateSaveToDb(data) {
      this.updateToDbData = data;
    },

    formatData(data) {
      if (!data) return '';
      // Deep clone to avoid modifying original data
      const cloned = JSON.parse(JSON.stringify(data));
      
      const truncate = (obj) => {
        if (!obj) return;
        
        ['url', 'videoUrl'].forEach(key => {
          if (obj[key] && typeof obj[key] === 'string' && obj[key].length > 100) {
            obj[key] = obj[key].substring(0, 100) + '...';
          }
        });
      };

      if (Array.isArray(cloned)) {
        cloned.forEach(item => truncate(item));
      } else {
        truncate(cloned);
      }

      return JSON.stringify(cloned, null, 2);
    }
  }
};
</script>

<style scoped>
label{
  margin-bottom: 0;
}
.playground-container {
  min-height: 100vh;
  background-color: #f8fafc;
  padding: 2rem;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  color: #334155;
}

.header {
  margin-bottom: 2rem;
}

.header h1 {
  font-size: 2rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}

.header p {
  color: #64748b;
  margin-top: 0.5rem;
}

.redirect-btn {
  margin-top: 1rem;
  padding: 0.5rem 1rem;
  background-color: #3b82f6;
  color: white;
  border: none;
  border-radius: 0.375rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease-in-out;
}

.redirect-btn:hover {
  background-color: #2563eb;
}

.card {
  background-color: white;
  border-radius: 1rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  border: 1px solid #e2e8f0;
  margin-bottom: 2rem;
  overflow: hidden;
}

.card-header {
  padding: 1.5rem;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #fff;
}

.card-summary {
  list-style: none;
  cursor: pointer;
  user-select: none;
}

.card-summary::-webkit-details-marker {
  display: none;
}

.card[open] .card-header {
  border-bottom: 1px solid #f1f5f9;
}

.card:not([open]) .card-header {
  border-bottom: none;
}

.section {
  border-bottom: 1px solid #e2e8f0;
  background-color: #fff;
}

.section:last-of-type {
  border-bottom: none;
}

.section-summary {
  padding: 1.25rem 1.5rem;
  background-color: #f8fafc;
  cursor: pointer;
  user-select: none;
  list-style: none;
  display: flex;
  align-items: center;
}

.section-summary::-webkit-details-marker {
  display: none;
}

.section-summary h3 {
  margin: 0;
}

.section-body {
  border-top: 1px solid #e2e8f0;
}

.card-header h2 {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  color: #0f172a;
}

.badge {
  padding: 0.25rem 0.75rem;
  background-color: #e0f2fe;
  color: #0369a1;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.controls-section {
  background-color: #f8fafc;
  padding: 1.5rem;
}

.controls-section h3, .component-section h3 {
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #94a3b8;
  margin-top: 0;
  margin-bottom: 1rem;
  font-weight: 700;
}

.controls-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1.5rem;
}

.control-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.control-group.full-width {
  grid-column: 1 / -1;
}

.control-group label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #475569;
}

.control-group input[type="text"],
.control-group input[type="number"],
.control-group select {
  padding: 0.5rem 0.75rem;
  border: 1px solid #cbd5e1;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  transition: border-color 0.15s ease-in-out;
  background-color: #fff;
}

.control-group input:focus,
.control-group select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.frf-preview {
  max-width: 480px;
}

.frf-empty-hint {
  margin: 0 0 0.75rem;
  font-size: 0.875rem;
  color: #94a3b8;
  font-style: italic;
}

.checkbox-group {
  flex-direction: row;
  align-items: flex-end; /* Align closer to the input line */
}

.checkbox-group label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  user-select: none;
}

.checkbox-group input[type="checkbox"] {
  width: 1rem;
  height: 1rem;
  cursor: pointer;
}

.component-section {
  padding: 1.5rem;
}

.component-wrapper {
  padding: 1.5rem;
  border: 2px dashed #e2e8f0;
  border-radius: 0.5rem;
  background-color: #fff;
}

.data-section {
  background-color: #1e293b;
  color: #e2e8f0;
  border-top: 1px solid #334155;
}

.data-section details {
  width: 100%;
}

.data-section summary {
  padding: 1rem 1.5rem;
  cursor: pointer;
  font-family: monospace;
  font-weight: 600;
  outline: none;
}

.data-section summary:hover {
  background-color: #334155;
}

.data-section pre {
  margin: 0;
  padding: 1.5rem;
  overflow-x: auto;
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
  font-size: 0.875rem;
  border-top: 1px solid #334155;
  white-space: pre-wrap;
  color: white;
}

.code-sample {
  margin: 0;
  padding: 1rem 1.25rem;
  overflow-x: auto;
  border-radius: 0.5rem;
  border: 1px solid #e2e8f0;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
  font-size: 0.875rem;
  line-height: 1.4;
  color: #0f172a;
}

.code-sample code {
  white-space: pre;
}
</style>
